import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import { APP_WORKSPACE } from "../constants/appWorkspace";
import { useAuth } from "./AuthContext";
import { clearWorkspace, readWorkspace, WorkspaceId, writeWorkspace } from "../utils/workspaceStorage";

type WorkspaceState = {
  activeWorkspace: WorkspaceId | null;
  setActiveWorkspace: (workspace: WorkspaceId) => void;
  clearActiveWorkspace: () => void;
};

const WorkspaceContext = createContext<WorkspaceState | undefined>(undefined);

/** Unified app — flat paths without workspace prefix. */
export function workspacePath(_workspace: WorkspaceId, suffix?: string): string {
  if (!suffix) return "/calendar";
  const normalized = suffix.startsWith("/") ? suffix.slice(1) : suffix;
  return `/${normalized}`;
}

export function workspaceRoot(_workspace: WorkspaceId): string {
  return "";
}

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const [activeWorkspace, setActiveWorkspaceState] = useState<WorkspaceId | null>(() => readWorkspace() ?? APP_WORKSPACE);

  const setActiveWorkspace = (workspace: WorkspaceId) => {
    writeWorkspace(workspace);
    setActiveWorkspaceState(workspace);
  };

  const clearActiveWorkspace = () => {
    clearWorkspace();
    setActiveWorkspaceState(null);
  };

  useEffect(() => {
    if (token && !activeWorkspace) {
      writeWorkspace(APP_WORKSPACE);
      setActiveWorkspaceState(APP_WORKSPACE);
    }
  }, [token, activeWorkspace]);

  useEffect(() => {
    if (!token && activeWorkspace) {
      clearWorkspace();
      setActiveWorkspaceState(null);
    }
  }, [token, activeWorkspace]);

  const value = useMemo(
    () => ({
      activeWorkspace,
      setActiveWorkspace,
      clearActiveWorkspace,
    }),
    [activeWorkspace],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspace = (): WorkspaceState => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within WorkspaceProvider");
  }
  return context;
};
